import { useEffect, useState } from "react";
import { Inbox, Loader2, Mail, Phone, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const STATUS = ["new", "contacted", "closed"];
const COLORS = {
  new: "bg-blue-100 text-blue-700",
  contacted: "bg-amber-100 text-amber-700",
  closed: "bg-emerald-100 text-emerald-700",
};

export const ReEnquiriesTab = () => {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("");

  const load = () => api.get("/admin/realestate/enquiries").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/realestate/enquiries/${id}`, { status }); await load(); toast.success(`Marked ${status}`); }
    catch { toast.error("Update failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try { await api.delete(`/admin/realestate/enquiries/${id}`); await load(); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  if (items === null) return <div className="flex min-h-[40vh] items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#C9A24B]" /></div>;

  const shown = filter ? items.filter((i) => i.status === filter) : items;

  return (
    <div data-testid="re-enquiries-tab">
      <h2 className="font-serif text-2xl font-semibold text-[#1A1615]">Enquiries</h2>
      <p className="mt-1 text-xs text-[#91837C]">{items.length} total · {items.filter((i) => i.status === "new").length} new</p>

      <div className="mt-5 flex flex-wrap gap-1.5">
        {[["", "All"], ...STATUS.map((s) => [s, s])].map(([id, label]) => (
          <button key={id || "all"} onClick={() => setFilter(id)} data-testid={`re-enquiries-filter-${id || "all"}`}
            className={`rounded-full px-3.5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] transition-colors ${filter === id ? "bg-[#1A1615] text-[#D4AF37]" : "bg-white text-[#91837C] hover:text-[#1A1615]"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="mt-5 space-y-3">
        {shown.map((e) => (
          <div key={e.id} className="glass rounded-2xl p-5" data-testid={`re-enquiry-${e.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-[#1A1615]">{e.name}</p>
                  <span className="rounded-full bg-[#F8F3E6] px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.1em] text-[#8a6d1f]">{(e.kind || "general").replace(/_/g, " ")}</span>
                </div>
                {e.subject && <p className="mt-0.5 text-xs font-medium text-[#5A504B]">{e.subject}</p>}
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#5A504B]">
                  <a href={`tel:${e.phone}`} className="inline-flex items-center gap-1 hover:text-[#B38B2D]"><Phone className="h-3 w-3" /> {e.phone}</a>
                  {e.email && <a href={`mailto:${e.email}`} className="inline-flex items-center gap-1 hover:text-[#B38B2D]"><Mail className="h-3 w-3" /> {e.email}</a>}
                  {e.page && <span className="inline-flex items-center gap-1 text-[#91837C]"><Inbox className="h-3 w-3" /> {e.page}</span>}
                </div>
                {e.message && <p className="mt-2 rounded-lg bg-[#F8F3E6] px-3 py-2 text-xs text-[#5A504B]">{e.message}</p>}
              </div>
              <div className="flex items-center gap-2">
                <span className={`rounded-full px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.12em] ${COLORS[e.status] || COLORS.new}`}>{e.status}</span>
                <button onClick={() => remove(e.id)} aria-label="Delete" data-testid={`re-enquiry-delete-${e.id}`} className="flex h-8 w-8 items-center justify-center rounded-full border border-red-200 text-red-500 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {STATUS.map((s) => (
                <button key={s} onClick={() => setStatus(e.id, s)} data-testid={`re-enquiry-${e.id}-status-${s}`}
                  className={`rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] transition-colors ${e.status === s ? "bg-[#1A1615] text-[#F3E5AB]" : "border border-[#E5D9CF] text-[#5A504B] hover:bg-[#F5EBE6]"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        ))}
        {!shown.length && <p className="glass rounded-2xl p-6 text-sm font-light text-[#91837C]">No enquiries yet.</p>}
      </div>
    </div>
  );
};
